/**
 * W07 — Commission display calculator (pure domain, zero framework imports).
 *
 * Acceptance (c): summary cards must match the PRD percentage formulas —
 * PRD §2.2 baseline yields and §2.3 pool guarantees are encoded here as
 * derived constants (60% × 10,000 = 6,000 · 25% × 12,500 = 3,125), so tests can
 * assert formula equality rather than hardcoded prose numbers.
 *
 * The interpretation of `ratePercent` (percent-of-transaction vs percent-of-pool)
 * is still awaiting backend sign-off — knowledge-tree §3 G0.4.
 */
import { TierLevel } from '../enums/TierLevel';
import { MIN_WITHDRAWAL } from './withdrawalFee';
import type { CommissionLedgerEntry, CommissionPoolType } from '../entities/CommissionLedgerEntry';

/** Minimum withdrawal (also gates the Withdraw CTA); re-exported from withdrawalFee.ts, the single source of truth. */
export const MIN_WITHDRAWAL_BDT = MIN_WITHDRAWAL;

/** PRD §2.2 transaction bases. */
export const BOOKING_BASE_BDT = 10_000;
export const EMI_BASE_BDT = 12_500;

/** PRD §2.3 pool guarantees — computed, not transcribed, so equality is testable. */
export interface PoolSpec {
  percent: number;
  base: number;
  amount: number;
}

export const BOOKING_POOL: PoolSpec = {
  percent: 60,
  base: BOOKING_BASE_BDT,
  amount: (60 * BOOKING_BASE_BDT) / 100, // BDT 6,000
};

export function emiPool(base: number = EMI_BASE_BDT): PoolSpec {
  return {
    percent: 25,
    base,
    amount: (25 * base) / 100,
  };
}

export const EMI_POOL: PoolSpec = emiPool(EMI_BASE_BDT); // BDT 3,125

export function poolOf(type: CommissionPoolType, emiBase: number = EMI_BASE_BDT): PoolSpec {
  if (type === 'BOOKING_POOL') return BOOKING_POOL;
  return emiBase === EMI_BASE_BDT ? EMI_POOL : emiPool(emiBase);
}

export interface TierRate {
  bookingPercent: number;
  emiPercent: number;
  bookingYield: number;
  emiYield: number;
}

export function tierRate(bookingPercent: number, emiPercent: number, emiBase: number = EMI_BASE_BDT): TierRate {
  return {
    bookingPercent,
    emiPercent,
    bookingYield: (bookingPercent * BOOKING_BASE_BDT) / 100,
    emiYield: (emiPercent * emiBase) / 100,
  };
}

export function getTierRates(emiBase: number = EMI_BASE_BDT): Record<TierLevel, TierRate | null> {
  return {
    [TierLevel.L1]: null,
    [TierLevel.L2]: tierRate(20, 10, emiBase), // ME  → 2,000 / (1,250 or 1,000)
    [TierLevel.L3]: tierRate(12, 5, emiBase),  // MM  → 1,200 / (625 or 500)
    [TierLevel.L4]: tierRate(10, 4, emiBase),  // AGM → 1,000 / (500 or 400)
    [TierLevel.L5]: tierRate(9, 3, emiBase),   // GM  →   900 / (375 or 300)
    [TierLevel.L6]: tierRate(9, 3, emiBase),   // Dir →   900 / (375 or 300)
    [TierLevel.ADMIN]: null,
  };
}

/** PRD §2.2 baseline incentive breakdown (tiers outside L2–L6 have no rate). */
export const PRD_TIER_RATES: Record<TierLevel, TierRate | null> = getTierRates(EMI_BASE_BDT);

// --- Aggregates ---------------------------------------------------------------

export interface PoolTotals {
  bookingAmount: number;
  emiAmount: number;
  bookingCount: number;
  emiCount: number;
}

/** Ledger totals per pool (non-finite amounts count as 0, malformed rows skipped). */
export function poolTotals(entries: CommissionLedgerEntry[] | undefined | null): PoolTotals {
  const totals: PoolTotals = { bookingAmount: 0, emiAmount: 0, bookingCount: 0, emiCount: 0 };
  for (const entry of Array.isArray(entries) ? entries : []) {
    if (!entry) continue;
    const amount = Number.isFinite(entry.amount) ? entry.amount : 0;
    if (entry.poolType === 'BOOKING_POOL') {
      totals.bookingAmount += amount;
      totals.bookingCount += 1;
    } else {
      totals.emiAmount += amount;
      totals.emiCount += 1;
    }
  }
  return totals;
}

export interface MonthlyBucket {
  /** `YYYY-MM` in Asia/Dhaka, locale-neutral axis key. */
  key: string;
  booking: number;
  emi: number;
  total: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Bangladesh Standard Time is a fixed UTC+6 (no DST). */
const DHAKA_OFFSET_MS = 6 * 3_600_000;

/** `YYYY-MM` of an instant as seen on the Dhaka calendar; null for unparseable input. */
export function dhakaMonthKey(input: string | number | Date): string | null {
  const ms = input instanceof Date ? input.getTime() : typeof input === 'number' ? input : Date.parse(input);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms + DHAKA_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
}

/** Oldest → newest `YYYY-MM` keys for the trailing `count` Dhaka months ending at `now`. */
export function trailingMonthKeys(count = 12, now: Date = new Date()): string[] {
  const n = Math.min(Math.max(Math.floor(Number(count)) || 12, 1), 24);
  const nowDhaka = new Date(now.getTime() + DHAKA_OFFSET_MS);
  const keys: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(nowDhaka.getUTCFullYear(), nowDhaka.getUTCMonth() - i, 1));
    keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`);
  }
  return keys;
}

/**
 * Trailing-month earnings buckets for MonthlyEarningsChart, bucketed on the Asia/Dhaka
 * calendar so a credit at 00:00:01 BST on the 1st lands in the month it was released.
 * Entries with unparseable dates or out-of-window timestamps are dropped (never crash
 * the chart), non-finite amounts count as 0.
 */
export function monthlyEarnings(
  entries: CommissionLedgerEntry[] | undefined | null,
  months = 12,
  now: Date = new Date(),
): MonthlyBucket[] {
  const buckets = trailingMonthKeys(months, now).map((key) => ({ key, booking: 0, emi: 0, total: 0 }));
  const index = new Map(buckets.map((b) => [b.key, b]));
  for (const entry of Array.isArray(entries) ? entries : []) {
    if (!entry) continue;
    const key = dhakaMonthKey(String(entry.createdAt));
    const bucket = key ? index.get(key) : undefined;
    if (!bucket) continue;
    const amount = Number.isFinite(entry.amount) ? entry.amount : 0;
    if (entry.poolType === 'BOOKING_POOL') bucket.booking += amount;
    else bucket.emi += amount;
    bucket.total += amount;
  }
  return buckets;
}

/** Highest-earning bucket, or null when nothing was earned in the window. */
export function bestMonth(buckets: MonthlyBucket[]): MonthlyBucket | null {
  let best: MonthlyBucket | null = null;
  for (const b of buckets) if (b.total > 0 && (!best || b.total > best.total)) best = b;
  return best;
}

/** Withdraw CTA gate (PRD §3: min BDT 500) — NaN/null/undefined all disable it. */
export function withdrawBlocked(availableBalance: unknown): boolean {
  const n = Number(availableBalance);
  return !(Number.isFinite(n) && n >= MIN_WITHDRAWAL_BDT);
}
